import { createContext, useEffect, useState } from 'react';
import { ID, isSchedulePending, Order, Pharmacy, UserOffer } from '../../Models';
import { ordersSubscription, userOffersSubscription } from './subscriptions';
import { toReadyForPickup } from './crud';

const DEFAULT_LAST_SNAPSHOT = new Date();
export type OrdersContextValue = {
    lastSnapshotAt: Date;
    emitted: Order[];
    assigned: Order[];
    approved: Order[];
    ordersById: Record<ID, Order>;
    loading: boolean;
};
const defaultContext: OrdersContextValue = {
    lastSnapshotAt: DEFAULT_LAST_SNAPSHOT,
    emitted: [] as Order[],
    assigned: [] as Order[],
    approved: [] as Order[],
    ordersById: {} as Record<ID, Order>,
    loading: true,
};
export const OrdersContext = createContext(defaultContext);

export const useOrders = (user: Pharmacy | undefined): OrdersContextValue => {
    const [userOffers, setUserOffer] = useState(null as null | Record<ID, UserOffer>);
    const [ordersSubscriptionState, setOrdersSubscriptionState] = useState({
        lastSnapshotAt: DEFAULT_LAST_SNAPSHOT,
        emitted: [] as Order[],
        assigned: [] as Order[],
        approved: [] as Order[],
    });
    const [editedOrders, setEditedOrders] = useState({
        emitted: [] as Order[],
        assigned: [] as Order[],
        approved: [] as Order[],
    });

    useEffect(() => {
        if (!user) return;
        const unsub1 = userOffersSubscription((offers) => {
            setUserOffer(offers);
        });
        const unsub2 = ordersSubscription(user.health_providers, (orders) => {
            if (
                !orders.emittedOrders.length &&
                !orders.assignedOrders.length &&
                !orders.approvedOrders.length
            ) {
                return;
            }
            setOrdersSubscriptionState({
                lastSnapshotAt: new Date(),
                emitted: orders.emittedOrders,
                assigned: orders.assignedOrders,
                approved: orders.approvedOrders,
            });
            Array.from(
                new Set(
                    [...orders.emittedOrders, ...orders.assignedOrders, ...orders.approvedOrders]
                        .map((o) => o.customer_id)
                        .sort(),
                ),
            );
        });
        // eslint-disable-next-line consistent-return
        return () => {
            unsub1();
            unsub2();
        };
    }, [user]);

    useEffect(() => {
        if (!user) {
            return;
        }
        if (userOffers) {
            const setOffer = <T extends Order>(order: T) => {
                const newOrder: T = { ...order };
                if (newOrder.id in userOffers) {
                    // eslint-disable-next-line no-param-reassign
                    newOrder.user_offer = userOffers[newOrder.id];
                } else {
                    // Podría ocurrir que una oferta previa sea eliminada, entonces la sacamos del campo
                    // eslint-disable-next-line no-param-reassign
                    newOrder.user_offer = undefined;
                }
                return newOrder;
            };
            setEditedOrders({
                emitted: ordersSubscriptionState.emitted.map(setOffer),
                assigned: ordersSubscriptionState.assigned.map(setOffer),
                approved: ordersSubscriptionState.approved.map(setOffer),
            });
        }
    }, [user, ordersSubscriptionState, userOffers]);

    if (user) {
        // Pedimos la moto en cuanto un pedido se abone
        editedOrders.approved
            .filter(
                (order) =>
                    order.state === 'approved' && order.user_offer && !isSchedulePending(order),
            )
            .forEach((order) => toReadyForPickup(order));
    }
    const ordersById: Record<ID, Order> = Object.fromEntries(
        [...editedOrders.emitted, ...editedOrders.assigned, ...editedOrders.approved].map(
            (order) => [order.id, order],
        ),
    );
    return {
        ordersById,
        emitted: editedOrders.emitted,
        assigned: editedOrders.assigned,
        approved: editedOrders.approved,
        lastSnapshotAt: ordersSubscriptionState.lastSnapshotAt,
        loading: ordersSubscriptionState.lastSnapshotAt === DEFAULT_LAST_SNAPSHOT,
    };
};
